import classnames from 'classnames';
import React, { useState, useRef } from 'react';
import { Icon } from '../../Icon/Icon';
import './../input.scss';
import { t } from '../../../locales/translate';
import { Spinner } from '../../Spinner/Spinner';
import { round } from '../../../util/util';
import { FormField } from '../../../model/form';
import { IconName } from '../../Icon/IconName';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';

export interface Props extends FormField<number | null> {
  ignoreValidationError?: boolean;
  maxLength?: number;
  icon?: IconName;
  actions?: React.ReactNode | React.ReactNodeArray;
  autoFocus?: boolean;
  displayOptional?: boolean;
  loading?: boolean;
  min?: number;
  max?: number;
  step?: number | string;
}

function isFull({
  focused,
  placeholder,
  formattedValue,
}: {
  focused?: boolean;
  placeholder?: string;
  formattedValue?: number;
}): boolean {
  return !!focused || !!placeholder || !!formattedValue || formattedValue === 0;
}

export function FormPercentageField({
  name,
  label,
  icon,
  actions,
  description,
  placeholder,
  errors,
  autoFocus,
  required,
  disabled,
  readOnly,
  min,
  max,
  step = 'any',
  value,
  displayOptional = true,
  loading,
  onChange,
  onFocus,
  onBlur,
  maxLength,
  valid,
  focused,
  touched: touchedProp,
}: Props): JSX.Element {
  const [touched, setTouched] = useState(touchedProp);
  const [editingValue, setEditingValue] = useState<string | undefined>(undefined);

  const displayErrors = touched && !valid;

  const parsedValue = round(parseFloat(`${value}`) * 100, 2);

  const formattedValue = !isNaN(parsedValue) ? parsedValue : undefined;
  const displayedValue = editingValue !== undefined ? editingValue : (formattedValue?.toString() ?? '');

  const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null);

  return (
    <div className={classnames('input', { 'input--with-icon': icon, 'input--disabled': disabled })}>
      {icon && <Icon name={icon} className="input__icon" />}
      <div
        className={classnames('input__container', {
          'input__container--active': focused,
          'input__container--full': isFull({ focused, placeholder, formattedValue }),
          'input__container--error': displayErrors,
        })}
      >
        <div className="input__input-wrapper">
          {label && (
            <label className="input__label" htmlFor={name}>
              {label}
              {displayOptional && !required && ` (${t.general.optional})`}
            </label>
          )}
          <input
            id={name}
            name={name}
            className="input__input"
            value={displayedValue}
            type="text"
            onChange={(event) => {
              if (readOnly) {
                event.preventDefault();
                return;
              }

              const rawValue = event.target.value;

              if (!/^\d*([.,]\d{0,2})?$/.test(rawValue)) {
                return;
              }

              const normalized = rawValue.replace(',', '.');
              const nextValue = parseFloat(normalized) / 100;

              setEditingValue(rawValue);
              if (onChange) {
                onChange((isNaN(nextValue) ? '' : nextValue) as number);
              }
              if (nextValue !== 0) {
                setTimeout(() => setTouched(true), 100);
              }
            }}
            onFocus={onFocus}
            onBlur={() => {
              setEditingValue(undefined);
              onBlur?.();
            }}
            autoFocus={autoFocus}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            ref={(ref) => {
              inputRef.current = ref;
            }}
            min={min}
            max={max}
            step={step}
            maxLength={maxLength}
          />
          {actions && <div className="input__actions">{actions}</div>}
          {displayErrors && <Icon name="alert-circle--sm" className="input__alert-circle" />}
          {loading && <Spinner className="input__loading" color="var(--color-text-label)" scale="0.6" />}
        </div>
        <div className="input__description-wrapper">
          {description && <div className="input__description">{description}</div>}
          <Collapse open={displayErrors}>
            {errors?.map((error) => {
              return (
                <div className="input__error-message" key={error}>
                  {t.errors[error] || error}
                </div>
              );
            })}
          </Collapse>
        </div>
      </div>
    </div>
  );
}
